import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSearch } from '@fortawesome/free-solid-svg-icons';
import { fetchLocalUser } from '../../services/api/userService';
import * as _ from 'lodash'; // Changed import to include type definitions

interface User {
  name: string;
  userId: string;
  designation: string;
}

interface AddUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUser: (userId: string) => Promise<{ valid: boolean }>;
}

// Function to highlight matched text in a string
const highlightMatch = (text: string, query: string): JSX.Element => {
  if (!query.trim()) return <>{text}</>;
  
  const regex = new RegExp(`(${_.escapeRegExp(query)})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, index) => 
        regex.test(part) ? <strong key={index}>{part}</strong> : <span key={index}>{part}</span>
      )}
    </>
  );
};

const AddUserDialog: React.FC<AddUserDialogProps> = ({ 
  isOpen, 
  onClose, 
  onAddUser
}) => {
  // Set debounce time as a constant within the component
  const DEBOUNCE_TIME = 500; // Configurable debounce time in milliseconds
  const [inputValue, setInputValue] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef(false);

  // Focus the input when dialog opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Create debounced fetch function using useRef to maintain reference across renders
  const debouncedFetchRef = useRef(
    _.debounce(async (searchText: string) => {
      if (searchText.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      
      try {
        // Call the API with the current input value
        const users = await fetchLocalUser(searchText.trim());
        setSuggestions(users.users);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching users:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_TIME)
  );

  // Fetch users based on input value
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return; // Skip fetching after selection
    }

    debouncedFetchRef.current(inputValue);

    // Cleanup function to cancel pending debounced calls
    return () => debouncedFetchRef.current.cancel();
  }, [inputValue]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation in dropdown
  const [activeIndex, setActiveIndex] = useState(-1);
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
    }
    // Arrow up
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
    }
    // Enter
    else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[activeIndex]);
    }
    // Enter with no selection but only one result
    else if (e.key === 'Enter' && activeIndex === -1 && suggestions.length === 1) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[0]);
    }
    // Escape
    else if (e.key === 'Escape') {
      e.preventDefault();
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setSelectedUserId('');
    setError(null);
    setActiveIndex(-1);
  };

  const handleSelectSuggestion = (user: User) => {
    justSelectedRef.current = true;
    setInputValue(user.userId);
    setSelectedUserId(user.userId);
    setShowSuggestions(false);
  
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleAddUser = async () => {
    // Use selectedUserId if available, otherwise extract from input value or use input value directly
    let userIdToAdd = selectedUserId;
    
    if (!userIdToAdd) {
      // Check if input value contains ID in parentheses like "John Doe (jdoe123)"
      const match = inputValue.match(/\(([^)]+)\)$/);
      userIdToAdd = match ? match[1].trim() : inputValue.trim();
    }
    
    if (userIdToAdd === '') {
      setError('User ID cannot be empty');
      return;
    }
    
    setIsValidating(true);
    setError(null);
    
    try {
      const response = await onAddUser(userIdToAdd);
      if (response.valid) {
        setInputValue('');
        setSelectedUserId('');
        setError(null);
        onClose();
      } else {
        setError('Invalid user ID or user already exists');
      }
    } catch (error) {
      setError('Error adding user');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCancel = (): void => {
    setInputValue('');
    setSelectedUserId('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      {isValidating && (
        <div className="fixed inset-0 bg-white bg-opacity-30 backdrop-blur-sm z-[60] pointer-events-none">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-auto">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-blue-500 text-4xl animate-spin mb-4"
            />
            <div className="bg-white bg-opacity-90 px-6 py-3 rounded-lg shadow-lg">
              <p className="text-gray-700 text-lg">
                Validating User ID...
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Add Local User</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 text-2xl w-8 h-8 flex items-center justify-center"
          >
            ×
          </button>
        </div>
        <div className="p-6">
          <div className="mb-4 relative">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onClick={() => {
                  if (justSelectedRef.current) {
                    justSelectedRef.current = false;
                    return; // Don't reopen suggestions right after selection
                  }
                  if (inputValue.trim().length >= 2) {
                    setShowSuggestions(true);
                  }
                }}
                placeholder="Enter User ID"
                className={`w-full px-4 py-2 newassistant-input ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {isLoading && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faSpinner} className="text-gray-400 animate-spin" />
                </div>
              )}
            </div>
            
            {showSuggestions && suggestions.length > 0 && (
              <div 
                ref={suggestionsRef}
                className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none"
              >
                {suggestions.map((user, index) => (
                  <div
                    key={user.userId}
                    className={`hover:bg-blue-100 ${activeIndex === index ? 'bg-blue-50' : ''}`}
                    onClick={() => handleSelectSuggestion(user)}
                  >
                    <div className="px-4 py-2 cursor-pointer">
                      <div className="font-medium">
                        {user.name ? 
                          <>
                            {highlightMatch(user.name, inputValue)} ({highlightMatch(user.userId, inputValue)})
                          </> : 
                          highlightMatch(user.userId, inputValue)
                        }
                      </div>
                      {user.designation && (
                        <div className="text-xs text-gray-500">
                          {highlightMatch(user.designation, inputValue)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {error && (
              <p className="mt-1 text-red-500 text-sm">{error}</p>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-white bg-gray-500 rounded-md hover:bg-gray-600 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddUser}
              disabled={isValidating}
              className="px-4 py-2 save-button rounded-md hover:bg-blue-600 focus:outline-none disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserDialog;